import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { ChartDataset } from 'chart.js';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { loadOnlineProductDemand, localDevToken, type OnlineDemandView } from '../api';
import type { ProductDemandResponse, ProductDemandRow } from '../types';
import { DEFAULT_RANGE, RANGE_OPTIONS } from '../ranges';

const num = (n: number) => n.toLocaleString('vi-VN');
const pct = (n: number, d: number) => (d ? Math.round((100 * n) / d) : 0) + '%';

const GROUP_LABEL: Record<string, string> = {
  LAPTOP: 'Laptop', PC: 'PC / Máy bộ', APPLE: 'Apple', MAN_HINH: 'Màn hình',
  LINH_KIEN: 'Linh kiện', GEAR: 'Gaming Gear', GHE_BAN: 'Ghế / Bàn',
  THIET_BI_MANG: 'Thiết bị mạng', PHU_KIEN: 'Phụ kiện', VAN_PHONG: 'Văn phòng',
  PHAN_MEM: 'Phần mềm', DICH_VU: 'Dịch vụ', OTHER: 'Khác', UNKNOWN: 'Chưa rõ',
};
const glabel = (g: string | null) => (g ? GROUP_LABEL[g] || g : '—');

type Gran = 'day' | 'week' | 'month';
const SPAN: Record<string, number> = { today: 0, yesterday: 1, '7d': 6, '28d': 27, '3m': 89, '6m': 179, all: 0 };
const DOW_VI = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const TREND_COLORS = ['#2563eb', '#16a34a', '#b45309', '#7c3aed'];

function parseDay(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}
function isoWeekKey(d: Date): string {
  const x = new Date(d); const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day); return x.toISOString().slice(0, 10);
}
function periodKey(dayStr: string, gran: Gran): string {
  if (gran === 'month') return dayStr.slice(0, 7);
  if (gran === 'week') { const d = parseDay(dayStr); return d ? isoWeekKey(d) : dayStr; }
  return dayStr;
}
function periodLabel(key: string, gran: Gran): string {
  if (gran === 'month') { const [y, m] = key.split('-'); return `T${Number(m)}/${y.slice(2)}`; }
  if (key.length >= 10) { const [, m, d] = key.split('-'); return gran === 'week' ? `Tuần ${d}/${m}` : `${d}/${m}`; }
  return key;
}
function countBy<T>(rows: T[], key: (r: T) => string | null): Array<[string, number]> {
  const m = new Map<string, number>();
  for (const r of rows) { const k = key(r); if (k) m.set(k, (m.get(k) || 0) + 1); }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}
function barDS(entries: Array<[string, number]>, active: string[], color: string): ChartDataset[] {
  return [{ label: 'Lượt hỏi', data: entries.map(e => e[1]),
    backgroundColor: entries.map(e => (active.length === 0 || active.includes(e[0]) ? color : color + '55')),
    borderRadius: 4, maxBarThickness: 46 } as unknown as ChartDataset];
}

// ---- unified demand-view shape both sources render ----
interface DRow { key: string; name: string; brand: string | null; group: string | null; segment: string | null; mentions: number; variants: number; closed: number; stock: number; high: number; resolved: boolean }
interface DV {
  mentions: number; groups: number; rawNames: number; resolved: number; brandFixed: number; generic: number;
  conversations: number; usable: number; normalized: number; categoryOnly: number; unmapped: number; orderLinked: number;
  trend: Array<{ key: string; n: number; resolved: number; stock: number }>;
  dow: number[]; groupBars: Array<[string, number]>; brandBars: Array<[string, number]>;
  catTrend: Array<{ group: string; data: number[] }>;
  sku: DRow[]; series: DRow[]; generic_rows: DRow[]; lost: DRow[];
  momentum: Array<{ name: string; brand: string | null; prior: number; recent: number; delta: number }>;
  spike: Array<{ name: string; brand: string | null; baseline: number; latest: number; ratio: number }>;
  channels: Array<[string, number]>;
}

type Filters = { group: string[]; store: string[]; brand: string[]; condition: string[]; period: string[] };
const EMPTY: Filters = { group: [], store: [], brand: [], condition: [], period: [] };

// ---------- offline DV (client-side from resolved per-mention rows) ----------
function buildOfflineDV(rows: ProductDemandRow[], gran: Gran): DV {
  const agg = (sel: (r: ProductDemandRow) => boolean): DRow[] => {
    const m = new Map<string, DRow & { _v: Set<string> }>();
    for (const r of rows) {
      if (!sel(r)) continue;
      let a = m.get(r.canonical_key);
      if (!a) { a = { key: r.canonical_key, name: r.canonical_name, brand: r.brand_final, group: r.group_code, segment: r.price_segment, mentions: 0, variants: 0, closed: 0, stock: 0, high: 0, resolved: r.is_resolved, _v: new Set() }; m.set(r.canonical_key, a); }
      a.mentions++; a._v.add(r.raw_name.toLowerCase());
      if (r.closed_won) a.closed++; if (r.stock_gap || r.demo_gap) a.stock++; if (r.high_value_flag) a.high++;
      if (r.is_resolved) a.resolved = true; if (!a.segment && r.price_segment) a.segment = r.price_segment;
    }
    return [...m.values()].map(a => ({ ...a, variants: a._v.size })).sort((x, y) => y.mentions - x.mentions);
  };
  const sku = agg(r => r.tier === 'model');
  const series = agg(r => r.tier === 'series');
  const generic_rows = agg(r => ['generic', 'brand_category', 'brand_only'].includes(r.tier));
  const lost = [...sku, ...series].filter(a => a.stock > 0).sort((a, b) => b.stock - a.stock).slice(0, 18);
  const tb = rows.filter(r => r.day);

  // trend
  const pm = new Map<string, { n: number; resolved: number; stock: number }>();
  for (const r of tb) { const k = periodKey(r.day as string, gran); const e = pm.get(k) || { n: 0, resolved: 0, stock: 0 }; e.n++; if (r.is_resolved) e.resolved++; if (r.stock_gap || r.demo_gap) e.stock++; pm.set(k, e); }
  const trend = [...pm.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([key, v]) => ({ key, ...v }));
  // dow Mon..Sun
  const dc = [0, 0, 0, 0, 0, 0, 0];
  for (const r of tb) { const d = parseDay(r.day); if (d) dc[d.getDay()]++; }
  const dow = [1, 2, 3, 4, 5, 6, 0].map(i => dc[i]);
  // category trend
  const groupBars = countBy(rows, r => r.group_code);
  const brandBars = countBy(rows.filter(r => r.brand_final), r => r.brand_final).slice(0, 14);
  const topG = groupBars.slice(0, 4).map(e => e[0]);
  const idx = new Map(trend.map((t, i) => [t.key, i]));
  const catTrend = topG.map(g => { const arr = new Array(trend.length).fill(0); for (const r of tb) if (r.group_code === g && r.day) { const i = idx.get(periodKey(r.day, gran)); if (i != null) arr[i]++; } return { group: g, data: arr }; });
  // momentum + spike
  const pkeys = trend.map(t => t.key);
  const mom = computeMomentum(tb, pkeys, gran);
  const spk = computeSpike(tb, pkeys, gran);

  return {
    mentions: rows.length, groups: sku.length + series.length + generic_rows.length,
    conversations: rows.length, usable: rows.filter(r => !['UNKNOWN', 'OTHER'].includes(r.group_code || '')).length,
    normalized: rows.filter(r => !r.is_resolved && !['generic', 'brand_category', 'brand_only'].includes(r.tier)).length,
    categoryOnly: rows.filter(r => ['generic', 'brand_category', 'brand_only'].includes(r.tier)).length,
    unmapped: rows.filter(r => ['UNKNOWN', 'OTHER'].includes(r.group_code || '')).length, orderLinked: 0,
    rawNames: new Set(rows.map(r => r.raw_name.toLowerCase())).size,
    resolved: rows.filter(r => r.is_resolved).length,
    brandFixed: rows.filter(r => r.brand_corrected).length,
    generic: rows.filter(r => ['generic', 'brand_category', 'brand_only'].includes(r.tier)).length,
    trend, dow, groupBars, brandBars, catTrend, sku, series, generic_rows, lost, momentum: mom, spike: spk,
    channels: [],
  };
}

function computeMomentum(tb: ProductDemandRow[], pkeys: string[], gran: Gran) {
  if (pkeys.length < 2) return [];
  const mid = pkeys[Math.floor(pkeys.length / 2)];
  const m = new Map<string, { name: string; brand: string | null; prior: number; recent: number }>();
  for (const r of tb) { if (r.tier !== 'model' && r.tier !== 'series') continue; let a = m.get(r.canonical_key); if (!a) { a = { name: r.canonical_name, brand: r.brand_final, prior: 0, recent: 0 }; m.set(r.canonical_key, a); } if (periodKey(r.day as string, gran) >= mid) a.recent++; else a.prior++; }
  return [...m.values()].map(a => ({ ...a, delta: a.recent - a.prior })).filter(a => a.recent >= 2 && a.delta > 0).sort((x, y) => y.delta - x.delta).slice(0, 12);
}
function computeSpike(tb: ProductDemandRow[], pkeys: string[], gran: Gran) {
  if (pkeys.length < 2) return [];
  const last = pkeys[pkeys.length - 1];
  const byKey = new Map<string, { name: string; brand: string | null; perP: Map<string, number> }>();
  for (const r of tb) { if (r.tier !== 'model' && r.tier !== 'series') continue; let a = byKey.get(r.canonical_key); if (!a) { a = { name: r.canonical_name, brand: r.brand_final, perP: new Map() }; byKey.set(r.canonical_key, a); } const k = periodKey(r.day as string, gran); a.perP.set(k, (a.perP.get(k) || 0) + 1); }
  const out: Array<{ name: string; brand: string | null; baseline: number; latest: number; ratio: number }> = [];
  for (const a of byKey.values()) { const latest = a.perP.get(last) || 0; if (latest < 3) continue; const prior = [...a.perP.entries()].filter(([k]) => k !== last); const baseline = prior.length ? prior.reduce((s, [, c]) => s + c, 0) / prior.length : 0; const ratio = latest / Math.max(baseline, 0.5); if (ratio >= 2) out.push({ name: a.name, brand: a.brand, baseline: Math.round(baseline * 10) / 10, latest, ratio: Math.round(ratio * 10) / 10 }); }
  return out.sort((x, y) => y.ratio - x.ratio).slice(0, 12);
}

// ---------- online DV (server already aggregated) ----------
function buildOnlineDV(v: OnlineDemandView, gran: Gran): DV {
  const k = v.kpis || { mentions: 0, conversations: 0, groups: 0, resolved: 0, usable: 0, normalized: 0, category_only: 0, unmapped: 0, order_linked: 0, brand_fixed: 0, generic_mentions: 0, raw_names: 0 };
  const mapRow = (s: Record<string, unknown>): DRow => ({
    key: String(s.canonical_key ?? ''), name: String(s.product ?? s.demand_term ?? ''),
    brand: (s.brand as string) ?? null, group: (s.group_code as string) ?? null, segment: (s.price_segment as string) ?? null,
    mentions: Number(s.mentions ?? 0), variants: Number(s.variants ?? 0), closed: Number(s.closed ?? 0),
    stock: Number(s.stock_gap ?? 0), high: Number(s.high_value ?? 0), resolved: s.resolved === true,
  });
  const sku = (v.sku || []).map(mapRow);
  const series = (v.series || []).map(mapRow);
  const generic_rows = (v.generic || []).map(mapRow);
  // re-bucket daily server trend to the chosen gran
  const pm = new Map<string, { n: number; resolved: number; stock: number }>();
  for (const t of (v.trend || [])) { const key = periodKey(t.period, gran); const e = pm.get(key) || { n: 0, resolved: 0, stock: 0 }; e.n += t.n; e.resolved += t.resolved; e.stock += t.stock; pm.set(key, e); }
  const trend = [...pm.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([key, val]) => ({ key, ...val }));
  // dow: server returns isodow 1..7 (Mon..Sun) → already Mon-first
  const dowArr = [0, 0, 0, 0, 0, 0, 0];
  for (const d of (v.dow || [])) { const i = d.dow - 1; if (i >= 0 && i < 7) dowArr[i] = d.n; }
  const groupBars: Array<[string, number]> = (v.groups || []).map(g => [g.group_code, g.n]);
  const brandBars: Array<[string, number]> = (v.brands || []).map(b => [b.brand, b.n]);
  const channels: Array<[string, number]> = (v.channels || []).map(c => [c.channel, c.n]);
  // category trend from server cattrend (group×day) → top4 groups
  const topG = groupBars.slice(0, 4).map(e => e[0]);
  const idx = new Map(trend.map((t, i) => [t.key, i]));
  const catTrend = topG.map(g => { const arr = new Array(trend.length).fill(0); for (const ct of (v.cattrend || [])) if (ct.group_code === g) { const i = idx.get(periodKey(ct.period, gran)); if (i != null) arr[i] += ct.n; } return { group: g, data: arr }; });
  const lost = [...sku, ...series].filter(a => a.stock > 0).sort((a, b) => b.stock - a.stock).slice(0, 18);
  const momentum = (v.momentum || []).map(m => ({ name: m.name, brand: m.brand, prior: m.prior, recent: m.recent, delta: m.recent - m.prior })).sort((x, y) => y.delta - x.delta);
  const spike = (v.spike || []).map(s => ({ name: s.name, brand: s.brand, baseline: s.baseline, latest: s.latest, ratio: s.ratio }));
  return {
    mentions: k.mentions, groups: k.groups, rawNames: k.raw_names,
    conversations: k.conversations, usable: k.usable, normalized: k.normalized,
    categoryOnly: k.category_only, unmapped: k.unmapped, orderLinked: k.order_linked,
    resolved: k.resolved, brandFixed: k.brand_fixed, generic: k.generic_mentions,
    trend, dow: dowArr, groupBars, brandBars, catTrend, sku, series, generic_rows, lost, momentum, spike, channels,
  };
}

export function ProductDemandScreen({ data, loaded, busy }: { data: ProductDemandResponse | null; loaded: boolean; busy: boolean }) {
  const [source, setSource] = useState<'offline' | 'online'>(() => (new URLSearchParams(window.location.search).get('source') === 'online' ? 'online' : 'offline'));
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [query, setQuery] = useState('');
  const [resolvedOnly, setResolvedOnly] = useState(false);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [gran, setGran] = useState<Gran>('day');
  const [onlineView, setOnlineView] = useState<OnlineDemandView | null>(null);
  const [onlineBusy, setOnlineBusy] = useState(false);
  const [onlineErr, setOnlineErr] = useState('');

  const all = data?.rows || [];

  const toggle = (dim: keyof Filters, val: string) => setFilters(f => ({ ...f, [dim]: f[dim].includes(val) ? f[dim].filter(v => v !== val) : [...f[dim], val] }));
  const clearAll = () => { setFilters(EMPTY); setQuery(''); setResolvedOnly(false); };
  const activeCount = Object.values(filters).reduce((s, a) => s + a.length, 0) + (query ? 1 : 0) + (resolvedOnly ? 1 : 0);
  const switchSource = (s: 'offline' | 'online') => { setSource(s); setFilters(EMPTY); setQuery(''); setResolvedOnly(false); setRange(DEFAULT_RANGE); };

  // online fetch (server-side aggregation) — re-fetch on range/filter change
  useEffect(() => {
    if (source !== 'online') return;
    let cancelled = false;
    setOnlineBusy(true); setOnlineErr('');
    loadOnlineProductDemand(localDevToken(), range, gran, { group: filters.group, brand: filters.brand, channel: filters.store, period: filters.period })
      .then(v => { if (!cancelled) { if (v.error) setOnlineErr(v.error); setOnlineView(v); } })
      .catch(e => { if (!cancelled) setOnlineErr(String((e as Error).message || e)); })
      .finally(() => { if (!cancelled) setOnlineBusy(false); });
    return () => { cancelled = true; };
  }, [source, range, gran, filters.group, filters.brand, filters.store, filters.period]);

  // offline client-side filtering
  const maxDay = useMemo(() => all.reduce((m, r) => (r.day && r.day > m ? r.day : m), ''), [all]);
  const cutoff = useMemo(() => { const days = SPAN[range] ?? 0; if (!days || !maxDay) return ''; const d = parseDay(maxDay); if (!d) return ''; d.setDate(d.getDate() - days); return d.toISOString().slice(0, 10); }, [range, maxDay]);
  const ranged = useMemo(() => (cutoff ? all.filter(r => r.day && r.day >= cutoff) : all), [all, cutoff]);
  const filtered = useMemo(() => ranged.filter(r => {
    if (filters.group.length && !filters.group.includes(r.group_code || '')) return false;
    if (filters.store.length && !filters.store.includes(r.store_code || '')) return false;
    if (filters.brand.length && !filters.brand.includes(r.brand_final || '')) return false;
    if (filters.condition.length && !filters.condition.includes(r.condition_demand || 'new')) return false;
    if (filters.period.length && !(r.day && filters.period.includes(periodKey(r.day, gran)))) return false;
    if (resolvedOnly && !r.is_resolved) return false;
    if (query && !(r.canonical_name + ' ' + r.raw_name).toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  }), [ranged, filters, query, resolvedOnly, gran]);

  const dv = useMemo<DV | null>(() => {
    if (source === 'online') return onlineView ? buildOnlineDV(onlineView, gran) : null;
    return buildOfflineDV(filtered, gran);
  }, [source, onlineView, filtered, gran]);

  const groupOpts = useMemo(() => (source === 'online' ? (onlineView?.groups || []).map(g => g.group_code) : [...new Set(all.map(r => r.group_code).filter(Boolean))] as string[]), [source, onlineView, all]);
  const storeOpts = useMemo(() => (source === 'online' ? (onlineView?.channels || []).map(c => c.channel) : [...new Set(all.map(r => r.store_code).filter(Boolean))].sort() as string[]), [source, onlineView, all]);
  const brandOpts = useMemo(() => (source === 'online' ? (onlineView?.brands || []).map(b => b.brand) : countBy(all.filter(r => r.brand_final), r => r.brand_final).map(e => e[0]).slice(0, 40)), [source, onlineView, all]);

  const isBusy = source === 'online' ? onlineBusy : (busy && !loaded);
  const storeLabel = source === 'online' ? 'Kênh' : 'Cửa hàng';

  const sourceToggle = (
    <section className="card space" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      <b>Kênh tư vấn</b>
      <div className="seg">
        <button type="button" className={`tab ${source === 'offline' ? 'active' : ''}`} onClick={() => switchSource('offline')}>Tư vấn tại cửa hàng</button>
        <button type="button" className={`tab ${source === 'online' ? 'active' : ''}`} onClick={() => switchSource('online')}>Tư vấn online</button>
      </div>
      <span className="muted">{source === 'online' ? 'Tổng hợp nhu cầu từ hội thoại online' : 'Tổng hợp nhu cầu tại showroom'}</span>
    </section>
  );

  if (source === 'online' && onlineErr && !dv) return <>{sourceToggle}<div className="card empty">Lỗi tải Online: {onlineErr}</div></>;
  if (isBusy && !dv) return <>{sourceToggle}<div className="card empty">Đang tải dữ liệu nhu cầu sản phẩm...</div></>;
  if (source === 'offline' && loaded && all.length === 0) return <>{sourceToggle}<div className="card empty">Chưa có dữ liệu nhu cầu sản phẩm tại cửa hàng.</div></>;
  if (!dv) return <>{sourceToggle}<div className="card empty">Chưa có dữ liệu.</div></>;

  return (
    <>
      {sourceToggle}
      <section className="card space">
        <div className="section-title">
          <h2>Nhu cầu sản phẩm — đã gộp các cách gọi khác nhau</h2>
          <span>{source === 'online' ? 'Tổng hợp theo sản phẩm chuẩn từ hội thoại online' : 'Tổng hợp theo sản phẩm chuẩn từ lượt tư vấn tại cửa hàng'}</span>
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label={source === 'online' ? 'Hội thoại có dòng nhu cầu' : 'Lượt hỏi sản phẩm'} value={num(source === 'online' ? dv.conversations : dv.mentions)} sub={source === 'online' ? `${num(dv.mentions)} dòng nhu cầu · ${(onlineView?.channels || []).length} kênh` : `${num(all.length)} tổng`} tone="blue" />
        <KpiCard label="Sản phẩm sau gộp" value={num(dv.groups)} sub={dv.rawNames ? `từ ${num(dv.rawNames)} tên thô → gộp ${pct(dv.rawNames - dv.groups, dv.rawNames)}` : 'nhóm chuẩn'} tone="cyan" />
        <KpiCard label="Đã xác định ngành hàng" value={pct(dv.usable, dv.mentions)} sub={`${num(dv.usable)}/${num(dv.mentions)} dòng dùng được cho báo cáo`} tone="green" />
        <KpiCard label="Khớp chính xác SKU catalog" value={pct(dv.resolved, dv.mentions)} sub={`${num(dv.resolved)} lượt · không tính khớp ngành/dòng`} tone="amber" />
        <KpiCard label="Chỉ xác định ngành" value={num(dv.categoryOnly)} sub={source === 'online' ? `${num(dv.orderLinked)} hội thoại khớp đơn bằng mã` : 'Chưa đủ mã sản phẩm'} tone="violet" />
        <KpiCard label="Chưa xác định" value={num(dv.unmapped)} sub="Hiển thị rõ, không còn bị rơi khỏi báo cáo" tone="red" />
      </section>

      {/* TREND */}
      <section className="card space" style={{ borderTop: '3px solid var(--blue)' }}>
        <div className="section-title" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h2>Xu hướng nhu cầu theo thời gian</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="seg">{RANGE_OPTIONS.map(o => <button key={o.v} type="button" className={`tab ${range === o.v ? 'active' : ''}`} onClick={() => { setRange(o.v); setFilters(f => ({ ...f, period: [] })); }}>{o.l}</button>)}</div>
            <div className="seg">{(['day', 'week', 'month'] as Gran[]).map(g => <button key={g} type="button" className={`tab ${gran === g ? 'active' : ''}`} onClick={() => { setGran(g); setFilters(f => ({ ...f, period: [] })); }}>{g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}</button>)}</div>
          </div>
        </div>
        <span className="muted">{num(dv.trend.reduce((s, t) => s + t.n, 0))} lượt có ngày · bấm cột để lọc theo kỳ {onlineBusy && source === 'online' ? '· đang tải...' : ''}</span>
        <div className="chart tall">
          <ChartBox
            labels={dv.trend.map(t => periodLabel(t.key, gran))}
            datasets={[
              { label: 'Lượt hỏi', type: 'bar', data: dv.trend.map(t => t.n), backgroundColor: dv.trend.map(t => (filters.period.length === 0 || filters.period.includes(t.key) ? '#2563eb' : '#2563eb55')), borderRadius: 4, maxBarThickness: 40, order: 3 } as unknown as ChartDataset,
              { label: 'Khớp SKU', type: 'line', data: dv.trend.map(t => t.resolved), borderColor: '#16a34a', backgroundColor: '#16a34a22', borderWidth: 2, tension: 0.35, pointRadius: 2, order: 1 } as unknown as ChartDataset,
              { label: 'Thiếu hàng', type: 'line', data: dv.trend.map(t => t.stock), borderColor: '#dc2626', backgroundColor: '#dc262622', borderWidth: 2, borderDash: [5, 3], tension: 0.35, pointRadius: 2, order: 2 } as unknown as ChartDataset,
            ]}
            onPointClick={({ index }) => dv.trend[index] && toggle('period', dv.trend[index].key)}
          />
        </div>
      </section>

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Chu kỳ theo thứ trong tuần</h2><span>khách hỏi nhiều nhất ngày nào</span></div>
          <div className="chart" style={{ height: 240 }}><ChartBox labels={DOW_VI.slice(1).concat(DOW_VI[0])} datasets={barDS(['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((l, i) => [l, dv.dow[i]] as [string, number]), [], '#7c3aed')} options={{ plugins: { legend: { display: false } } }} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Xu hướng theo ngành hàng (top 4)</h2><span>ngành nào đang tăng</span></div>
          <div className="chart" style={{ height: 240 }}><ChartBox labels={dv.trend.map(t => periodLabel(t.key, gran))} datasets={dv.catTrend.map((c, i) => ({ label: glabel(c.group), type: 'line', data: c.data, borderColor: TREND_COLORS[i], backgroundColor: TREND_COLORS[i] + '22', borderWidth: 2, tension: 0.35, pointRadius: 2 } as unknown as ChartDataset))} /></div>
        </div>
      </section>

      <section className="grid split space atop">
        <div className="card" style={{ borderTop: '3px solid var(--green)' }}>
          <div className="section-title"><h2>SKU bứt tốc (đang tăng nhanh)</h2><span>nửa kỳ gần vs nửa kỳ trước · Δ lượt hỏi</span></div>
          <div className="scroll" style={{ maxHeight: 320 }}>
            <table>
              <thead><tr><th>Sản phẩm</th><th>Thương hiệu</th><th className="right">Kỳ trước</th><th className="right">Kỳ này</th><th className="right">Δ tăng</th></tr></thead>
              <tbody>{dv.momentum.length === 0 ? <tr><td colSpan={5} className="muted">Chưa đủ dữ liệu nhiều kỳ để tính bứt tốc.</td></tr>
                : dv.momentum.map((a, i) => (<tr key={i}><td>{a.name}</td><td className="muted">{a.brand || '—'}</td><td className="right tnum">{a.prior}</td><td className="right tnum"><b>{a.recent}</b></td><td className="right tnum"><Tag tone="green">▲ {a.delta}</Tag></td></tr>))}</tbody>
            </table>
          </div>
        </div>
        <div className="card" style={{ borderTop: '3px solid var(--amber)' }}>
          <div className="section-title"><h2>SKU cao đột biến (so với nền kỳ trước)</h2><span>kỳ mới nhất vượt {'≥'}2× mức nền của chính SKU</span></div>
          <div className="scroll" style={{ maxHeight: 320 }}>
            <table>
              <thead><tr><th>Sản phẩm</th><th>Thương hiệu</th><th className="right">Nền</th><th className="right">Kỳ này</th><th className="right">Bội số</th></tr></thead>
              <tbody>{dv.spike.length === 0 ? <tr><td colSpan={5} className="muted">Không có SKU cao đột biến theo bộ lọc.</td></tr>
                : dv.spike.map((a, i) => (<tr key={i}><td>{a.name}</td><td className="muted">{a.brand || '—'}</td><td className="right tnum">{a.baseline}</td><td className="right tnum"><b>{a.latest}</b></td><td className="right tnum"><Tag tone="amber">{a.ratio}×</Tag></td></tr>))}</tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="card space" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <b>Lọc</b>
        <select value="" onChange={e => e.target.value && toggle('group', e.target.value)}><option value="">+ Ngành hàng</option>{groupOpts.map(g => <option key={g} value={g}>{glabel(g)}</option>)}</select>
        <select value="" onChange={e => e.target.value && toggle('store', e.target.value)}><option value="">+ {storeLabel}</option>{storeOpts.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <select value="" onChange={e => e.target.value && toggle('brand', e.target.value)}><option value="">+ Thương hiệu</option>{brandOpts.map(b => <option key={b} value={b}>{b}</option>)}</select>
        {source === 'offline' && <>
          <select value="" onChange={e => e.target.value && toggle('condition', e.target.value)}><option value="">+ Tình trạng</option><option value="new">Mới</option><option value="used">Cũ</option></select>
          <input placeholder="Tìm sản phẩm..." value={query} onChange={e => setQuery(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--ink)' }} />
          <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={resolvedOnly} onChange={e => setResolvedOnly(e.target.checked)} /> chỉ SKU khớp catalog</label>
        </>}
        {activeCount > 0 && <button type="button" className="pill ghost" onClick={clearAll}>Xóa lọc ({activeCount})</button>}
      </section>

      {activeCount > 0 && (
        <section className="card space"><div className="filter-pills">
          {(Object.keys(filters) as Array<keyof Filters>).flatMap(dim => filters[dim].map(val => (
            <button key={`${dim}:${val}`} type="button" className="pill filter" onClick={() => toggle(dim, val)}>
              <span className="muted">{dim === 'group' ? 'Ngành' : dim === 'store' ? (source === 'online' ? 'Kênh' : 'CH') : dim === 'brand' ? 'Brand' : dim === 'period' ? 'Kỳ' : 'TT'}:</span> {dim === 'group' ? glabel(val) : dim === 'period' ? periodLabel(val, gran) : val} <X size={12} />
            </button>
          )))}
        </div></section>
      )}

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Nhu cầu theo ngành hàng</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: Math.max(180, dv.groupBars.length * 26 + 60) }}><ChartBox labels={dv.groupBars.map(e => glabel(e[0]))} datasets={barDS(dv.groupBars, filters.group, '#2563eb')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => dv.groupBars[index] && toggle('group', dv.groupBars[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Thương hiệu được hỏi (đã chuẩn hoá)</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: Math.max(180, dv.brandBars.length * 26 + 60) }}><ChartBox labels={dv.brandBars.map(e => e[0])} datasets={barDS(dv.brandBars, filters.brand, '#0891b2')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => dv.brandBars[index] && toggle('brand', dv.brandBars[index][0])} /></div>
        </div>
      </section>

      <section className="card space" style={{ borderTop: '3px solid var(--cyan)' }}>
        <div className="section-title"><h2>Top sản phẩm khách hỏi (đã gộp biến thể)</h2><span>{num(dv.sku.length)} SKU chuẩn · ✓ = khớp catalog · "Chốt (HT)" = lượt hỏi trong hội thoại đã chốt, không phải tỉ lệ chuyển đổi của SKU</span></div>
        <div className="scroll" style={{ maxHeight: 460 }}>
          <table>
            <thead><tr><th>Sản phẩm chuẩn</th><th>Thương hiệu</th><th>Ngành</th><th>Phân khúc</th><th className="right">Lượt hỏi</th><th className="right">Biến thể gộp</th><th className="right" title="Lượt hỏi trong hội thoại đã chốt — KHÔNG phải tỉ lệ chuyển đổi của riêng SKU">Chốt (HT)</th><th className="right">Thiếu hàng</th><th className="right">Giá trị cao</th></tr></thead>
            <tbody>{dv.sku.slice(0, 60).map(a => (
              <tr key={a.key}><td>{a.resolved ? <span title="khớp catalog" style={{ color: 'var(--green)' }}>✓ </span> : ''}{a.name}</td><td className="muted">{a.brand || '—'}</td><td className="muted">{glabel(a.group)}</td><td className="muted">{a.segment || '—'}</td><td className="right tnum"><b>{num(a.mentions)}</b></td><td className="right tnum">{a.variants > 1 ? <Tag tone="cyan">{a.variants}</Tag> : '1'}</td><td className="right tnum">{a.closed || '—'}</td><td className="right tnum">{a.stock ? <Tag tone="red">{a.stock}</Tag> : '—'}</td><td className="right tnum">{a.high ? <Tag tone="violet">{a.high}</Tag> : '—'}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Nhu cầu theo dòng sản phẩm (series)</h2><span>{num(dv.series.length)} dòng · khách hỏi cả line</span></div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table><thead><tr><th>Dòng / line</th><th>Thương hiệu</th><th>Ngành</th><th className="right">Lượt hỏi</th><th className="right">Biến thể</th></tr></thead>
              <tbody>{dv.series.slice(0, 30).map(a => (<tr key={a.key}><td>{a.name}</td><td className="muted">{a.brand || '—'}</td><td className="muted">{glabel(a.group)}</td><td className="right tnum"><b>{num(a.mentions)}</b></td><td className="right tnum">{a.variants}</td></tr>))}</tbody></table>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Nhu cầu chung &amp; theo thương hiệu</h2><span>{num(dv.generic_rows.length)} từ khoá · KHÔNG phải SKU cụ thể</span></div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table><thead><tr><th>Từ khoá nhu cầu</th><th>Ngành</th><th>Thương hiệu</th><th className="right">Lượt hỏi</th></tr></thead>
              <tbody>{dv.generic_rows.slice(0, 30).map(a => (<tr key={a.key}><td>{a.name}</td><td className="muted">{glabel(a.group)}</td><td className="muted">{a.brand || '—'}</td><td className="right tnum"><b>{num(a.mentions)}</b></td></tr>))}</tbody></table>
          </div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title"><h2>Cầu chưa đáp ứng (thiếu hàng / demo)</h2><span>cơ hội nhập hàng · trưng bày</span></div>
        <div className="scroll" style={{ maxHeight: 320 }}>
          <table><thead><tr><th>Sản phẩm khách cần</th><th>Thương hiệu</th><th>Ngành</th><th className="right">Lượt bị chặn</th></tr></thead>
            <tbody>{dv.lost.length === 0 ? <tr><td colSpan={4} className="muted">Không có sản phẩm bị chặn theo bộ lọc.</td></tr>
              : dv.lost.map(a => (<tr key={a.key}><td>{a.name}</td><td className="muted">{a.brand || '—'}</td><td className="muted">{glabel(a.group)}</td><td className="right tnum"><Tag tone="red">{a.stock}</Tag></td></tr>))}</tbody></table>
        </div>
      </section>
    </>
  );
}
